import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Clock, FileText, CheckSquare, Terminal } from 'lucide-react';

interface ProcedureCloneCatalogueActifsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProcedureCloneCatalogueActifsModal({ isOpen, onClose }: ProcedureCloneCatalogueActifsModalProps) {
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const toggleStep = (id: number) => {
    const next = new Set(completedSteps);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCompletedSteps(next);
  };

  if (!isOpen) return null;

  const steps = [
    { id: 1, label: 'Copier les fichiers (frontend + backend)', done: completedSteps.has(1) },
    { id: 2, label: 'Configuration (client.ts)', done: completedSteps.has(2) },
    { id: 3, label: 'Route (App.tsx)', done: completedSteps.has(3) },
    { id: 4, label: 'Page client + handleCardClick', done: completedSteps.has(4) },
    { id: 5, label: 'Card dans clientData.ts', done: completedSteps.has(5) },
    { id: 6, label: 'Backend (server/index.js)', done: completedSteps.has(6) },
    { id: 7, label: 'Dépendances (cookie-parser)', done: completedSteps.has(7) },
    { id: 8, label: 'Variables .env', done: completedSteps.has(8) },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="bg-linear-to-r from-amber-600 via-orange-600 to-rose-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Copy className="w-7 h-7 text-amber-200" />
                <h2 className="text-2xl font-bold">Cloner le Catalogue des Actifs</h2>
              </div>
              <p className="text-white/90 text-sm">
                D&apos;un client à un autre. Sources de référence : <strong>PRIsME Ville de Saint-Lin</strong> ou <strong>POB</strong>.
              </p>
              <p className="text-amber-100 text-xs mt-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <strong>Moins de 10 min</strong>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Commande rapide */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Terminal className="w-4 h-4 text-amber-600" />
              Commande rapide (PowerShell)
            </h3>
            <pre className="bg-gray-900 text-amber-100 p-4 rounded-lg text-sm overflow-x-auto">
{`# Depuis le projet PRIsME_POB
.\\scripts\\clone-catalogue.ps1 -TargetProject "C:\\path\\to\\PRIsME_Client"`}
            </pre>
          </div>

          {/* Checklist manuelle */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-amber-600" />
              Checklist manuelle
            </h3>
            <ul className="space-y-2">
              {steps.map((s) => (
                <li key={s.id} className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleStep(s.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      s.done ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-300 hover:border-amber-400'
                    }`}
                  >
                    {s.done && <span className="text-xs">✓</span>}
                  </button>
                  <span className={s.done ? 'text-gray-500 line-through' : ''}>{s.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 1. Copier les fichiers */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">1. Copier les fichiers</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`# Frontend
cp src/services/catalogueActifsService.ts [PROJET]/src/services/
cp src/components/CatalogueActifCard.tsx [PROJET]/src/components/
cp src/components/CatalogueActifsSection.tsx [PROJET]/src/components/
cp src/pages/CatalogueActifs.tsx [PROJET]/src/pages/

# Backend
cp server/catalogueMasterApi.js [PROJET]/server/`}
            </pre>
          </div>

          {/* 2. Configuration */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">2. Configuration (src/config/client.ts)</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`export const CLIENT_DISPLAY_NAME = 'Nom du Client';
export const IS_PRISME_CLIENT = true;`}
            </pre>
          </div>

          {/* 3. Route */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">3. Route (src/App.tsx)</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`import CatalogueActifs from './pages/CatalogueActifs';
// ...
<Route path="/catalogue-actifs" element={<CatalogueActifs />} />`}
            </pre>
          </div>

          {/* 4. Page client */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">4. Page client (src/pages/Client[XXX].tsx)</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`import { useNavigate } from 'react-router-dom';
// ...
const navigate = useNavigate();
const handleCardClick = (componentName: string) => {
  if (componentName === 'CatalogueActifs') {
    navigate('/catalogue-actifs');
    return;
  }
  setSelectedCard(componentName);
};`}
            </pre>
          </div>

          {/* 5. Card */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">5. Card (src/data/[client]Data.ts)</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`{
  id: 9,
  title: 'Catalogue des actifs',
  category: 'Actifs TI',
  description: 'Vue visuelle des actifs TI...',
  componentName: 'CatalogueActifs'
}`}
            </pre>
          </div>

          {/* 6. Backend */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">6. Backend (server/index.js)</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`import * as catalogueMasterApi from './catalogueMasterApi.js';
// Ajouter les 2 routes (voir PROCEDURE_CLONAGE_CATALOGUE.md)`}
            </pre>
          </div>

          {/* 7. Dépendances */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">7. Dépendances</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`cd server
npm install cookie-parser`}
            </pre>
          </div>

          {/* 8. Variables .env */}
          <div>
            <h4 className="font-medium text-gray-800 mb-2">8. Variables .env</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`PRISME_MASTER_API_BASE=https://apibzprisme.bzinc
OAUTH_CLIENT_ID=...
OAUTH_CLIENT_SECRET=...`}
            </pre>
          </div>

          <p className="text-sm text-gray-600 flex items-center gap-2">
            <FileText className="w-4 h-4 shrink-0" />
            Procédure complète : <code className="bg-gray-100 px-1 rounded">PROCEDURE_CLONAGE_CATALOGUE.md</code>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
